import React, { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Form, Formik } from "formik"
import * as Yup from "yup"
import { useNavigate } from "react-router-dom"

import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import Spinners from "components/Shared/Spinners"

import {
  createRole,
  getPermissions,
  getSingleRole,
  updateRole,
} from "../../../store/actions/roleAction"
import { getJobTitlesList } from "store/actions/commonAction"
import {
  getFieldRequiredMessage,
  getValidArabicMessage,
} from "components/Common/validationMessage"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"

const formValues = {
  name: "",
  name_ar: "",
}

const arabicNameOrNumberPattern = /^[\u0600-\u06FF0-9\s\p{P}\p{S}]+$/u

const AddEditRole = ({ mode }) => {
  const [permissions, setPermissions] = useState({})
  const [selectAll, setSelectAll] = useState(false)
  const [selectAllJobs, setSelectAllJobs] = useState(false)
  const [initialValues, setInitialValues] = useState(formValues)
  const [showSelectJob, setShowSelectJobs] = useState(false)
  const [jobIds, setJobIds] = useState([])

  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const {
    permissions: permissionsList,
    role,
    loading,
  } = useSelector(state => state.RolesAndPermissions)
  const { jobTitlesList } = useSelector(state => state.Common)
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const uuid = searchParams.get("uuid")

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(getFieldRequiredMessage()),
    name_ar: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
  })

  document.title =
    mode === "add"
      ? t("Create Role And Permissions | Yamama")
      : t("Update Role And Permissions | Yamama")

  const populateData = () => {
    const { job_ids, stations, ...rest } = role.permissions
    if (job_ids) {
      setShowSelectJobs(true)
      setJobIds(job_ids)
    }

    setPermissions(rest)
    setSelectAll(role.has_full_permssion)
    setInitialValues({ name: role.name, name_ar: role.name_ar })
    if (role.has_full_permssion) {
      handleSelectAll(role.has_full_permssion)
    }
  }

  const getAllData = () => {
    dispatch(getPermissions())
    dispatch(getJobTitlesList())
  }

  const handlePermissionChange = (permissionKey, subPermission) => {
    const exists = permissions[permissionKey]

    if (!exists || (exists && exists.length === 0)) {
      if (subPermission === "can_assign_job") {
        setShowSelectJobs(true)
        setJobIds([])
      }

      return setPermissions({
        ...permissions,
        [permissionKey]: [subPermission],
      })
    }

    const existsSubPermission = exists.includes(subPermission)
    if (!existsSubPermission && subPermission === "can_assign_job") {
      setShowSelectJobs(true)
      setJobIds([])
    } else if (existsSubPermission && subPermission === "can_assign_job") {
      setShowSelectJobs(false)
      setJobIds([])
    }

    let updatedPermissions = {}

    if (existsSubPermission) {
      updatedPermissions = {
        ...permissions,
        [permissionKey]: exists.filter(item => item !== subPermission),
      }

      if (updatedPermissions[permissionKey].length === 0) {
        delete updatedPermissions[permissionKey]
      }
    } else {
      updatedPermissions = {
        ...permissions,
        [permissionKey]: [...exists, subPermission],
      }
    }

    setPermissions(updatedPermissions)
    checkIfAllSelected(updatedPermissions)
  }

  const checkIfAllSelected = updatedPermissions => {
    const allSelected = permissionsList.every(
      perm => updatedPermissions[perm.key]?.length === perm.permissions.length
    )

    setSelectAll(allSelected)
  }

  const handleSelectAll = value => {
    setSelectAll(value)

    if (!value) {
      return setPermissions({})
    }

    let obj = {}
    permissionsList.forEach(perm => {
      obj[perm.key] = perm.permissions.map(permission => permission.key)
    })

    setPermissions(obj)
  }

  const handleSelectAllJobs = value => {
    if (value) {
      setJobIds(jobTitlesList.map(job => job.id))
    } else {
      setJobIds([])
    }

    setSelectAllJobs(value)
  }

  const handleJobIdSelect = id => {
    const exists = jobIds.includes(id)
    let updatedJobIds = exists
      ? jobIds.filter(jobId => jobId !== id)
      : [...jobIds, id]

    setJobIds(updatedJobIds)

    setSelectAllJobs(updatedJobIds.length === jobTitlesList.length)
  }

  const onSubmit = values => {
    if (!selectAll && Object.entries(permissions).length === 0) {
      return dispatch(
        showAlert({
          text: t("Please select atleast one permission"),
          type: "error",
        })
      )
    }

    if (showSelectJob && jobIds.length === 0) {
      return dispatch(
        showAlert({
          text: t("Please select atleast one job"),
          type: "error",
        })
      )
    }

    const obj = {
      ...values,
      has_full_permssion: selectAll,
      permissions: selectAll
        ? {}
        : {
            ...permissions,
            ...(showSelectJob && { job_ids: jobIds }),
          },
    }

    if (mode === "add") {
      dispatch(createRole(obj))
        .then(action => {
          const { statusCode, success, message, data } = action.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    } else
      dispatch(updateRole({ id: role.uuid, data: obj }))
        .then(action => {
          const { statusCode, success, message, data } = action.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
  }

  useEffect(() => {
    getAllData()
  }, [])

  useEffect(() => {
    if (uuid) dispatch(getSingleRole(uuid))
  }, [uuid])

  useEffect(() => {
    if (role && uuid) populateData()
  }, [role, uuid])

  if (loading) return <Spinners />

  return (
    <Container fluid className="mt-4 page-content">
      <h4 className="card-title">
        <BackButton />
        {mode === "add" ? t("Add Role") : t("Update Role")}
      </h4>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched }) => (
          <Form>
            <Card>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <CustomLabel
                      htmlFor="name_english"
                      label={t("Name (English)")}
                      isRequired={true}
                    />
                    <CustomField
                      name="name"
                      type="text"
                      placeholder={t("Name in english")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={6}>
                    <CustomLabel
                      htmlFor="name_arabic"
                      label={t("Name (Arabic)")}
                      isRequired={true}
                    />
                    <CustomField
                      name="name_ar"
                      type="text"
                      placeholder={t("Name in arabic")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
            <h4 className="card-title ">{t("Permissions")}</h4>
            <Row className="mb-2">
              <Col xs={12} className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectAll}
                  style={{ height: 20, width: 20 }}
                  onClick={() => handleSelectAll(!selectAll)}
                />
                <span style={{ color: "#495057" }}>
                  {t("Select All Permissions")}
                </span>
              </Col>
            </Row>
            {!selectAll && (
              <Row className="align-items-stretch">
                {permissionsList.map(perm => {
                  return (
                    <Col md={4} key={perm.id} className="mt-3 d-flex">
                      <Card className="w-100 h-100">
                        <CardBody>
                          <h5 className="fw-bold">{perm.label}</h5>
                          {perm.permissions.map(permission => (
                            <div
                              key={permission.id}
                              className="d-flex align-items-center gap-2 mb-1"
                            >
                              <input
                                type="checkbox"
                                style={{ height: 20, width: 20 }}
                                checked={
                                  permissions[perm.key]?.includes(
                                    permission.key
                                  ) || false
                                }
                                onClick={() => {
                                  if (selectAll) {
                                    handleSelectAll(false)
                                  }
                                  handlePermissionChange(
                                    perm.key,
                                    permission.key
                                  )
                                }}
                              />
                              &nbsp;{permission.label}
                            </div>
                          ))}
                        </CardBody>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            )}

            {showSelectJob && !selectAll && (
              <Card className="mt-5">
                <CardBody>
                  <h4 className="card-title ">{t("Select Jobs")}</h4>
                  <Row className="mb-3">
                    <Col xs={12} className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectAllJobs}
                        style={{ height: 20, width: 20 }}
                        onClick={() => handleSelectAllJobs(!selectAllJobs)}
                      />
                      <span>{t("Select All Jobs")}</span>
                    </Col>
                  </Row>
                  <Row>
                    {jobTitlesList?.map(item => (
                      <Col
                        md={4}
                        xs={12}
                        key={item.id}
                        className="d-flex align-items-center gap-2 mb-1"
                      >
                        <input
                          type="checkbox"
                          style={{ height: 20, width: 20 }}
                          checked={jobIds.includes(item.id) || false}
                          onClick={() => handleJobIdSelect(item.id)}
                        />
                        &nbsp;
                        {i18n.language === "sa" ? item.name_ar : item.name}
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            )}

            <Row>
              <Col>
                <Col lg={12}>
                  <div className="g_end mt-2 gap-3 cis_btn">
                    <button
                      className="main_btn disabled-button fit_content px-4"
                      type="button"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      className="main_btn fit_content px-4"
                      type="submit"
                      disabled={loading}
                    >
                      {mode === "add" ? t("Create") : t("Update")}
                    </button>
                  </div>
                </Col>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default AddEditRole
